import * as React from "react";
import { cn } from "@/lib/utils";
import { CircleInfoBold, X } from "@/components/icons";

export interface InlineNoticeBannerProps {
  children: React.ReactNode;
  /** 넘기면 우측에 [닫기 ×] 가 노출된다. 없으면 닫을 수 없는 안내다 */
  onClose?: () => void;
  className?: string;
}

/**
 * 화면 안쪽에 놓이는 info 톤 안내 배너.
 *
 * 토스트와 달리 사라지지 않고, 규칙·우선순위처럼 조작 중 계속 참고할 내용을 담는다.
 * dismiss 상태는 호출부가 소유한다 — 영구 저장 여부는 화면 성격에 따라 다르다.
 *
 * 본문 타이포는 호출부가 정한다 — 한 줄 안내와 2단 안내(요약 + 강조 규칙)가 섞인다.
 *
 * @example
 * ```tsx
 * {noticeOpen && (
 *   <InlineNoticeBanner onClose={() => setNoticeOpen(false)}>
 *     적용 우선순위: 충전기 요금단가 &gt; 충전소 요금단가 &gt; 기본 요금단가
 *   </InlineNoticeBanner>
 * )}
 * ```
 */
export function InlineNoticeBanner({
  children,
  onClose,
  className,
}: InlineNoticeBannerProps) {
  return (
    <div
      className={cn(
        "flex items-start gap-2.5 rounded-xl bg-status-available/[0.08] p-4",
        className,
      )}
    >
      <span className="shrink-0">
        <CircleInfoBold size={24} color="status-available" />
      </span>
      <div className="typo-body-1-medium min-w-0 flex-1 text-labels-primary">
        {children}
      </div>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label="안내 닫기"
          className="typo-label-1-medium inline-flex shrink-0 cursor-pointer items-center gap-0.5 text-labels-alternative"
        >
          닫기
          <X size={16} color="labels-alternative" />
        </button>
      )}
    </div>
  );
}

InlineNoticeBanner.displayName = "InlineNoticeBanner";
